import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { useTranslation } from "react-i18next";

import {
  listEpisodeTickets,
  createStripeTicketCheckoutSession,
  getUserEmail,
  createPayPayTicketQrCode,
  getUnconfirmedPayPayPayment,
} from "utils/api";
import ButtonDialog from "default/components/ButtonDialog";
import Loading from "default/components/Loading";

import { COLORS } from "constants/colors";
import AppLayout from "works/chi/components/AppLayout";
import EpisodeError from "works/chi/components/EpisodeError";
import InputEmail from "default/components/InputEmail";
import PayPayButton from "works/chi/components/PayPayButton";
import Footer from "works/chi/components/Footer";
import StyledButton from "works/chi/components/StyledButton";
import ThaiCatchPhrase from "works/chi/components/ThaiCatchPhrase";
import { lineAccountLinks } from "constants/links";
import { formatPrice } from "utils/formatPrice";
import { THAI_FONT_FAMILY } from "theme/thaiButtonStyles";

// Make sure to call `loadStripe` outside of a component’s render to avoid
// recreating the `Stripe` object on every render.
const stripeApiKey = process.env.REACT_APP_STRIPE_API_KEY;
const stripePromise = loadStripe(stripeApiKey);
const TICKET_SERIF_FONT_FAMILY =
  '"Noto Serif JP", "Noto Serif Thai", serif';
const THAI_BUY_BUTTON_FONT_FAMILY = '"Sukhumvit Set", "Kanit", sans-serif';
const KANIT_FONT_FAMILY = '"Kanit", sans-serif';

const renderThaiStoryLine = (line) =>
  line.split(/(\d+)/).map((part, index) =>
    /^\d+$/.test(part) ? (
      <Box
        component="span"
        key={`${part}-${index}`}
        sx={{
          "--thai-font-family": KANIT_FONT_FAMILY,
          fontFamily: KANIT_FONT_FAMILY,
        }}
      >
        {part}
      </Box>
    ) : (
      part
    )
  );

function TicketBox({ ticket, onClick, isThai }) {
  const { t, i18n } = useTranslation(["chi", "common"]);
  const formattedPrice = formatPrice(ticket.price, ticket.currency_code);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
      }}
    >
      {/* <img
          src={`/chi/tickets/title/${ticket.ticket_key}.svg`}
          alt="ticket description"
          width="100%"
          style={{ display: "block" }} // 隙間をなくす
        /> */}

      <Typography
        variant="h4"
        sx={{
          fontFamily: '"Noto Serif JP", serif',
          fontSize: "21px",
          fontWeight: isThai ? 500 : "bold",
          color: COLORS.TEXT_WHITE,
          mt: 1,
        }}
      >
        {ticket.name}
      </Typography>

      {ticket.ticket_key === "v2_full_purchase" && (
        <Typography
          variant="h4"
          sx={{
            fontFamily: '"Noto Serif JP", serif',
            fontSize: "12px",
            fontWeight: isThai ? 300 : 400,
            color: COLORS.TEXT_WHITE,
            mt: "10px",
          }}
        >
          {t("chi:TicketBox.full_purchase")}
        </Typography>
      )}

      {ticket.ticket_key === "v2_trial" && (
        <Typography
          variant="h4"
          sx={{
            fontFamily: '"Noto Serif JP", serif',
            fontSize: "12px",
            fontWeight: isThai ? 300 : 400,
            color: COLORS.TEXT_WHITE,
            mt: "10px",
          }}
        >
          {t("chi:TicketBox.trial_purchase")}
        </Typography>
      )}

      <Typography
        variant="h4"
        sx={{
          fontFamily: '"Noto Serif JP", serif',
          fontSize: "22px",
          fontWeight: "bold",
          color: COLORS.TEXT_WHITE,
          mt: 2,
        }}
      >
        {isThai && ticket.currency_code === "THB" ? (
          <>
            {formattedPrice.slice(0, -1)}
            <Box
              component="span"
              sx={{
                "--thai-font-family": THAI_BUY_BUTTON_FONT_FAMILY,
                fontFamily: THAI_BUY_BUTTON_FONT_FAMILY,
                fontWeight: 400,
              }}
            >
              ฿
            </Box>
          </>
        ) : (
          formattedPrice
        )}
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
        }}
      >
        <StyledButton
          variant="contained"
          sx={{
            width: "34%",
            fontSize: "20px",
            fontWeight: "bold",
            mt: 2,
            mb: 1,
            ...(isThai && {
              "html[lang^='th'] &": {
                "--thai-font-family": THAI_BUY_BUTTON_FONT_FAMILY,
                fontFamily: THAI_BUY_BUTTON_FONT_FAMILY,
                fontWeight: 700,
              },
            }),
          }}
          onClick={() => onClick(ticket.ticket_key)}
        >
          {t("chi:button.buy")}
        </StyledButton>

        {(ticket.ticket_key === "v2_trial" ||
          ticket.ticket_key === "v2_trial_to_full") && (
            <Typography
              variant="h4"
              sx={{
                fontFamily: '"Noto Serif JP", serif',
                fontSize: "13px",
                fontWeight: isThai ? 500 : "bold",
                color: COLORS.TEXT_WHITE,
              }}
            >
              {/* ※初回限定{" "} */}
              {t("chi:TicketBox.first_time_only")}
            </Typography>
          )}

        {ticket.ticket_key === "v2_repurchase_no_memory" && (
          <Typography
            variant="h4"
              sx={{
                fontFamily: '"Noto Serif JP", serif',
                fontSize: "13px",
                fontWeight: isThai ? 300 : 400,
              color: COLORS.TEXT_WHITE,
            }}
          >
            {/* ※記憶がなくなります */}
            {t("chi:TicketBox.memories_lost")}
          </Typography>
        )}

        {ticket.ticket_key === "v2_repurchase_with_memory" && (
          <Typography
            variant="h4"
              sx={{
                fontFamily: '"Noto Serif JP", serif',
                fontSize: "13px",
                fontWeight: isThai ? 300 : 400,
              color: COLORS.TEXT_WHITE,
            }}
          >
            {/* ※記憶が引き継がれます */}
            {t("chi:TicketBox.memories_inherited")}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function Tickets() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const lng = queryParams.get("lng") || "ja"; // デフォルトは日本語

  const { t, i18n } = useTranslation(["translation", "common", "chi"]);

  const isThai = lng.startsWith("th");
  const fontFamily = lng.startsWith("ja")
    ? '"Noto Serif JP", serif'
    : lng.startsWith("zh")
      ? '"LXGW WenKai TC", serif'
      : lng.startsWith("ko")
        ? '"Gowun Dodum", serif'
        : isThai
          ? THAI_FONT_FAMILY
        : '"Cormorant Infant", serif';

  // クエリパラメータを取得
  const soulId = queryParams.get("soul_id");

  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openCheckPayment, setOpenCheckPayment] = useState(false);
  const [merchantPaymentId, setMerchantPaymentId] = useState(null);
  const [openAnnouncement, setOpenAnnouncement] = useState(true);
  const [episodeTickets, setEpisodeTickets] = useState([]);
  const [isError, setIsError] = useState(false);
  const [emailRequired, setEmailRequired] = useState(false);
  const [selectedEpisodeId, setSelectedEpisodeId] = useState(null);
  const [selectedTicketKey, setSelectedTicketKey] = useState(null);
  const [purchaseMethod, setPurchaseMethod] = useState(null);

  const getSoulName = (soulId) => {
    return soulId.split("_")[0];
  };

  const titleVisual = t(`chi:ticket_visual.${getSoulName(soulId)}`, {
    lng,
    defaultValue: ["rafal", "badeni", "jolenta-girl", "oczy"].includes(getSoulName(soulId))
      ? "chapter1"
      : "chapter2",
  });

  const handleAnnouncementClose = () => {
    setOpenAnnouncement(false);
  };

  const handleCheckPaymentClose = () => {
    setOpenCheckPayment(false);
  };

  const purchaseWithPayPay = async () => {
    try {
      navigate(
        `/chi/confirm-paypay?soul_id=${soulId}&episode_id=${selectedEpisodeId}&ticket_key=${selectedTicketKey}&lng=${lng}`
      );
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const purchaseWithStripe = async () => {
    try {
      const { session_id } = await createStripeTicketCheckoutSession(
        soulId,
        selectedEpisodeId,
        selectedTicketKey
      );
      const stripe = await stripePromise;
      // Stripe Checkoutページを開く
      const { error } = await stripe.redirectToCheckout({
        sessionId: session_id,
      });
      if (error) {
        console.error("Error:", error);
      }
    } catch (error) {
      console.error("Error:", error);
    }
    setIsProcessing(false);
  };

  const navigateToPurchase = async () => {
    if (purchaseMethod === "stripe") {
      purchaseWithStripe();
    } else if (purchaseMethod === "paypay") {
      purchaseWithPayPay();
    }
  };

  const onClickPurchaseMethod = async (purchaseMethod) => {
    setIsProcessing(true);
    try {
      const email = await getUserEmail();

      if (!email.email_address) {
        window.scrollTo(0, 0);
        setPurchaseMethod(purchaseMethod);
        setOpenDialog(false);
        setEmailRequired(true);
        setIsProcessing(false);
        return;
      }
    } catch (error) {
      console.error("Error:", error);
      setIsProcessing(false);
      return;
    }

    if (purchaseMethod === "stripe") {
      purchaseWithStripe();
    } else if (purchaseMethod === "paypay") {
      purchaseWithPayPay();
    }
  };

  const onClickPurchase = async (episodeId, ticketKey) => {
    setSelectedEpisodeId(episodeId);
    setSelectedTicketKey(ticketKey);
    setOpenDialog(true);
  };

  const initTickets = async (soulId) => {
    try {
      const { merchant_payment_id: merchantPaymentId } =
        await getUnconfirmedPayPayPayment(soulId);
      if (merchantPaymentId) {
        setOpenCheckPayment(true);
        setMerchantPaymentId(merchantPaymentId);
      }
    } catch (error) {
      console.error("Error:", error);
    }

    try {
      const episodeTickets = await listEpisodeTickets(soulId);
      // Chiでは記憶を引き継がない再購入を全言語で非表示にする。
      // バックエンドのチケット定義は変更せず、旧・現行キーを表示対象から除外する。
      setEpisodeTickets(
        episodeTickets.filter(
          (ticket) =>
            ticket.ticket_key !== "v2_repurchase_no_memory" &&
            ticket.ticket_key !== "repurchase_no_memory"
        )
      );
    } catch (error) {
      // 修正箇所: エピソードが購入されていない場合の処理を追加
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const listOtherAccountLinks = (character) => {
    // characterと異なるもののみ返す配列
    return Object.entries(lineAccountLinks["chi"][lng] || {})
      .filter(([key]) => key !== character)
      .map(([name, url]) => ({ name, url }));
  };

  const otherAccountLinks = listOtherAccountLinks(getSoulName(soulId));

  // 初回ロード時
  useEffect(() => {
    initTickets(soulId);
  }, []);

  if (isLoading) {
    return (
      <AppLayout>
        <Loading />
      </AppLayout>
    );
  }

  if (isError) {
    return (
      <AppLayout>
        <EpisodeError />
      </AppLayout>
    );
  }

  if (emailRequired) {
    return (
      <AppLayout>
        <InputEmail soulId={soulId} onClickPurchase={navigateToPurchase} />
      </AppLayout>
    );
  }

  // episodeTicketsのticket_keyに"repurchase"の文字列が含まれているものが一つでもあるかどうか
  const isRepurchase = episodeTickets.some((ticket) =>
    ticket.ticket_key.includes("repurchase")
  );

  return (
    <Box
      sx={{
        // backgroundColor: COLORS.TEXT_BLACK, // 明るいグレー
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundImage: "url(/chi/background.png)", // publicフォルダの画像を使用
        backgroundSize: "100% auto",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0,
          width: "100%",
          mb: 2,
          ...(isThai && {
            "--thai-font-family": THAI_FONT_FAMILY,
          }),
        }}
      >
        <Box sx={{ position: "relative", width: "100%" }}>
          {/* 背景画像 */}
          <img
            src={titleVisual === "chapter1"
              ? "/chi/title_image.png"
              : "/chi/title_image_chapter2.png"
            }
            alt="title image"
            width="100%"
            style={{ display: "block" }} // 隙間をなくす
          />

          {/* タイトルロゴ */}
          {titleVisual === "chapter1" && (
            <Box
              sx={{
                position: "absolute",
                top: "0%",
                left: "50%", // 左から50%の位置に配置
                transform: "translate(-50%, 0%)", // 中央に正確に配置
                width: "100%", // ロゴの幅を調整
              }}
            >
              {isThai ? (
                <ThaiCatchPhrase />
              ) : (
                <img
                  src={`/chi/catch_phrase/${lng}.svg`}
                  alt="catch phrase"
                  width="100%" // 親のBoxに合わせる
                  style={{ display: "block" }} // 隙間をなくす
                />
              )}
            </Box>
          )}
        </Box>

        {/* Story */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            width: "100%",
            mt: 1,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontFamily: TICKET_SERIF_FONT_FAMILY,
              fontSize: "36px",
              fontWeight: "bold",
              color: COLORS.TEXT_WHITE,
            }}
          >
            {!isRepurchase && "Story"}
            {isRepurchase && "Epilogue"}
          </Typography>

          <Typography
            variant="h4"
            sx={{
              fontFamily: TICKET_SERIF_FONT_FAMILY,
              fontSize: "16px",
              fontWeight: isThai ? 500 : "bold",
              color: COLORS.TEXT_WHITE,
              mt: 1,
            }}
          >
            - {t(`chi:character.${getSoulName(soulId)}`)} -
          </Typography>

          {!isRepurchase && (
            <Typography
              variant="h4"
              sx={{
                mt: 2,
                fontFamily,
                fontSize: isThai ? "clamp(14px, 3.35vw, 17px)" : "17px",
                fontWeight: 400,
                color: isThai ? "#ffffff" : COLORS.TEXT_WHITE,
                textAlign: "center",
                lineHeight: isThai ? 1.7 : 2.4,
                letterSpacing: isThai ? "normal" : "0.1em",
              }}
            >
              {t(`chi:story.${getSoulName(soulId)}`, {
                returnObjects: true,
              }).map((line, index) => {
                return (
                  <Box
                    component="span"
                    key={`${line}-${index}`}
                    sx={{ display: "block", whiteSpace: isThai ? "nowrap" : "normal" }}
                  >
                    {line
                      ? isThai
                        ? renderThaiStoryLine(line)
                        : line
                      : "\u00a0"}
                  </Box>
                );
              })}
            </Typography>
          )}

          {isRepurchase && (
            <Typography
              variant="h4"
              sx={{
                mt: 2,
                fontFamily,
                fontSize: isThai ? "clamp(14px, 3.35vw, 17px)" : "17px",
                fontWeight: 400,
                color: isThai ? "#ffffff" : COLORS.TEXT_WHITE,
                textAlign: "center",
                lineHeight: isThai ? 1.7 : 2.4,
                letterSpacing: isThai ? "normal" : "0.1em",
              }}
            >
              {t(`chi:epilogue.${getSoulName(soulId)}`, {
                returnObjects: true,
              }).map((line, index) => {
                return (
                  <Box
                    component="span"
                    key={`${line}-${index}`}
                    sx={{ display: "block", whiteSpace: isThai ? "nowrap" : "normal" }}
                  >
                    {line
                      ? isThai
                        ? renderThaiStoryLine(line)
                        : line
                      : "\u00a0"}
                  </Box>
                );
              })}
            </Typography>
          )}
        </Box>

        <Typography
          variant="h4"
          sx={{
            fontFamily: '"Noto Serif JP", serif',
            fontSize: "36px",
            fontWeight: "bold",
            color: COLORS.TEXT_WHITE,
            mt: 10,
          }}
        >
          Ticket
        </Typography>
        {episodeTickets.length === 0 && (
          <Typography
            variant="h4"
            sx={{
              fontFamily,
              fontSize: "16px",
              fontWeight: isThai ? 500 : "bold",
              color: COLORS.TEXT_WHITE,
              whiteSpace: "pre-line",
              lineHeight: 1.8,
              mt: 3,
            }}
          >
            {t("common:error.ticket_not_available")}
          </Typography>
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 5,
            mt: 3,
            width: "100%",
          }}
        >
          {episodeTickets.map((ticket) => (
            <TicketBox
              key={ticket.ticket_key}
              ticket={ticket}
              isThai={isThai}
              onClick={() =>
                onClickPurchase(ticket.episode_id, ticket.ticket_key)
              }
            />
          ))}
        </Box>
      </Box>
      {/* Others Section */}
      {otherAccountLinks.length > 0 && <Box
        sx={{
          width: "100%",
          py: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(0,0,0,0.4)",
          mt: 1,
        }}
      >
        <Typography
          variant="h4"
          sx={{
            color: COLORS.TEXT_WHITE,
            fontFamily: '"Cormorant Infant", serif',
            fontSize: "36px",
            fontWeight: "bold",
            letterSpacing: "0.06em",
            mb: 3,
          }}
        >
          Others
        </Typography>
        {otherAccountLinks.map((char, idx, arr) => (
          <Typography
            key={char.name}
            component="a"
            href={char.url}
            sx={{
              color: COLORS.TEXT_WHITE,
              fontFamily: isThai ? KANIT_FONT_FAMILY : '"Noto Serif JP", serif',
              fontSize: "20px",
              fontWeight: "bold",
              textDecoration: "none",
              mb: idx === arr.length - 1 ? 0 : 2,
              cursor: "pointer",
              transition: "opacity 0.2s",
              "&:hover": { opacity: 0.7 },
            }}
          >
            {t(`chi:character.${char.name}`)}
          </Typography>
        ))}
      </Box>}

      <Footer soulId={soulId} />

      {/* <ButtonDialog
        open={openAnnouncement}
        title={`交流期間変更のお知らせ`}
        handleClose={handleAnnouncementClose}
        description={"同価格でより\n高い会話密度をご提供するため\n\n4月23日からの新規ご購入商品に関して交流期間を短縮し\n毎日最低でも30回以上の会話回数お約束する\n内容になりました。\n\n7日間の交流 → 3日間の交流\n28日間の交流 → 10日間の交流\n\n詳細はこちら"}
        url={"https://tinyurl.com/ym48mjk9"}
      /> */}

      <ButtonDialog
        open={openCheckPayment}
        title={"PayPay購入による処理が正常に完了していない可能性があります。"}
        handleClose={handleCheckPaymentClose}
        buttons={[
          {
            label: "購入状況を確認",
            onClick: () => {
              navigate(
                `/chi/check-paypay?soul_id=${soulId}&merchant_payment_id=${merchantPaymentId}&lng=${lng}` // merchantPaymentIdをクエリパラメータとして渡す
              );
            },
          },
        ]}
      />

      <ButtonDialog
        open={openDialog}
        title={`${t("chi:button.dialog.select_purchase")}`}
        paperSx={isThai ? { "--thai-font-family": THAI_FONT_FAMILY } : undefined}
        titleSx={isThai ? { fontWeight: 500 } : undefined}
        rawButtons={[
          <Box key="credit-card" sx={{ width: "100%" }}>
            <StyledButton
              variant="contained"
              onClick={() => onClickPurchaseMethod("stripe")}
              aria-describedby={isThai ? "truemoney-payment-note" : undefined}
              sx={{
                width: "100%",
                fontSize: "16px",
                color: COLORS.TEXT_BLACK,
                border: `2px solid ${COLORS.TEXT_BLACK}`,
                background: isProcessing ? "white" : "transparent",
              }}
              disabled={isProcessing}
            >
              {t("chi:button.purchase_option_credit_card")}
            </StyledButton>
            {isThai && (
              <Typography
                id="truemoney-payment-note"
                sx={{
                  mt: 0.75,
                  fontSize: "12px",
                  fontWeight: 400,
                  lineHeight: 1.5,
                  color: "#6B6B6B",
                  textAlign: "center",
                }}
              >
                {t("chi:button.purchase_option_truemoney_notice")}
              </Typography>
            )}
          </Box>,
          lng === "ja" && (
            <Box key="paypay" sx={{ width: "100%" }}>
              <PayPayButton
                onClick={() => onClickPurchaseMethod("paypay")}
                disabled={isProcessing}
              />

              <Typography
                sx={{
                  mt: 1,
                  fontSize: "12px",
                  color: COLORS.TEXT_BLACK,
                  textAlign: "center",
                  fontFamily: '"Noto Serif JP", serif',
                  whiteSpace: "pre-line",
                }}
              >
                {t("chi:button.purchase_option_paypay")}
              </Typography>
            </Box>
          ),
        ]}
        handleClose={() => {
          setOpenDialog(false);
        }}
      />
    </Box>
  );
}

export default Tickets;
